import React, { useState } from "react";
import { alpha, styled } from "@mui/material/styles";
import { Tooltip } from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";
import Menu, { MenuProps } from "@mui/material/Menu";
import MuiMenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    color="textSecondary"
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "left",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "left",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(0.5),
    minWidth: 180,
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

interface MenuItem extends MenuItemProps {
  icon?: React.ReactNode;
  text: string;
  onClick: () => void;
}

interface MenuButtonProps {
  menuItems: MenuItem[];
  tooltipText?: string;
}

interface ButtonVariantProps {
  variant: "button";
  buttonProps?: ButtonProps;
}

interface IconButtonVariantProps extends IconButtonProps {
  variant: "iconButton";
  iconButtonProps?: IconButtonProps;
}

export default function MenuButton(
  props: React.PropsWithChildren<MenuButtonProps & (ButtonVariantProps | IconButtonVariantProps)>,
) {
  const { children, menuItems, tooltipText, variant } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleOpenMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {variant === "button" && (
        <Tooltip title={tooltipText}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleOpenMenuClick}
            endIcon={<KeyboardArrowDownIcon />}
            {...props.buttonProps}
          >
            {children}
          </Button>
        </Tooltip>
      )}
      {variant === "iconButton" && (
        <Tooltip title={tooltipText}>
          <IconButton color="primary" size="large" onClick={handleOpenMenuClick} {...props.iconButtonProps}>
            {children}
          </IconButton>
        </Tooltip>
      )}
      <StyledMenu anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
        {menuItems.map((menuItem) => {
          const { text, icon, onClick, ...menuItemProps } = menuItem;
          return (
            <MuiMenuItem
              key={`menuItem-${text}`}
              {...menuItemProps}
              onClick={() => {
                onClick();
                closeMenu();
              }}
              disableRipple
              sx={{ py: 1.5 }}
            >
              {icon}
              {text}
            </MuiMenuItem>
          );
        })}
      </StyledMenu>
    </>
  );
}
